import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BSMLogo } from '../../assets/svgIconsList';
import bsmMobileLogo from '../../assets/bsm_mobile_logo.png';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useDispatch } from 'react-redux';
import './Navbar.css';
import { setApplicationId, setSessionId } from '../../redux/actions';
import { preserveAllUrlParams, restoreDynamicParamsFromStorage } from '../../utils/urlParams';

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    width: '100%', // Ensure it's taking the full width
    margin: 0, // Remove default margin
  },
  appBar: {
    width: '100%', // Ensure AppBar is 100% width
    zIndex: 1201, // Default z-index for AppBar is 1100; ensure it's on top if modified
  }
});

const NavBar = () => {
  const dispatch=useDispatch()
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation(); 

  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved === "Spanish" ? "Spanish" : "English";
  });
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("language", lang);
    window.dispatchEvent(new CustomEvent("langChanged", { detail: lang }));
  }, [lang]);

  useEffect(() => {
    if (!isLangMenuOpen) return;

    const handlePointerDown = (e) => {
      if (!langMenuRef.current) return;
      if (langMenuRef.current.contains(e.target)) return;
      setIsLangMenuOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLangMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLangMenuOpen]);

  const contentByLang = useMemo(
    () => ({
      English: {
        toggleLabel: "English",
      },
      Spanish: {
        toggleLabel: "Spanish",
      },
    }),
    []
  );

  const otherLangOption = useMemo(() => {
    const otherLang = lang === "English" ? "Spanish" : "English";
    return {
      value: otherLang,
      label: contentByLang[otherLang].toggleLabel,
    };
  }, [contentByLang, lang]);

  const navigateHome = () => {
    localStorage.removeItem("session_id")
    localStorage.removeItem("application_id")
    localStorage.removeItem("formType")
    localStorage.removeItem("formData")

    dispatch(setSessionId(null));
    dispatch(setApplicationId(null))
    
    // Preserve all URL parameters (both registered and dynamic)
    const searchParams = preserveAllUrlParams(location.search);
    
    // Restore dynamic parameters from localStorage if not in URL
    restoreDynamicParamsFromStorage(searchParams);
    
    // Ensure required parameters have defaults if missing
    if (!searchParams.has("lar")) {
      const larFromStorage = localStorage.getItem("lar") || "admin";
      searchParams.set("lar", larFromStorage);
    }
    if (!searchParams.has("utm_source")) {
      const sourceFromStorage = localStorage.getItem("utm_source") || "direct";
      searchParams.set("utm_source", sourceFromStorage);
    }
    
    // Handle optional parameters from localStorage if not in URL
    if (!searchParams.has("utm_term")) {
      const kwFromStorage = localStorage.getItem("utm_term");
      if (kwFromStorage) {
        searchParams.set("utm_term", kwFromStorage);
      }
    }
    
    if (!searchParams.has("utm_campaign")) {
      const campaignFromStorage = localStorage.getItem("utm_campaign");
      if (campaignFromStorage) {
        searchParams.set("utm_campaign", campaignFromStorage);
      }
    }
    
    if (!searchParams.has("utm_type")) {
      const utmTypeFromStorage = localStorage.getItem("utm_type");
      if (utmTypeFromStorage) {
        searchParams.set("utm_type", utmTypeFromStorage);
      }
    }
    
    navigate(`${process.env.REACT_APP_BASE_URL}?${searchParams.toString()}`)
  }
  return (
    <div className={classes.root}>
      <AppBar position="fixed" color="default" className={classes.appBar}>
        <Toolbar style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div className='logo'  onClick={() => navigateHome()}>
            {!isMobile ? <BSMLogo/> : <img src={bsmMobileLogo} alt="Mobile Logo" className='mobile-logo' />}
          </div>
          <div className="navbar-language-toggle-container" ref={langMenuRef}>
            <button
              type="button"
              className="navbar-language-toggle"
              aria-haspopup="menu"
              aria-expanded={isLangMenuOpen}
              onClick={() => setIsLangMenuOpen((v) => !v)}
            >
              <span className="navbar-language-toggle__icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M2 12H22"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 2C14.7614 4.73858 16.3333 8.25 16.3333 12C16.3333 15.75 14.7614 19.2614 12 22C9.23858 19.2614 7.66667 15.75 7.66667 12C7.66667 8.25 9.23858 4.73858 12 2Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="navbar-language-toggle__label">{contentByLang[lang].toggleLabel}</span>
              <span className="navbar-language-toggle__chevron" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>

            {isLangMenuOpen && (
              <div className="navbar-language-menu" role="menu">
                <button
                  type="button"
                  className="navbar-language-menu__item"
                  role="menuitem"
                  onClick={() => {
                    setLang(otherLangOption.value);
                    setIsLangMenuOpen(false);
                  }}
                >
                  {otherLangOption.label}
                </button>
              </div>
            )}
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default NavBar;
